// Step 3 · Tartalom: the letter, its subject line, the A/B arms, and — in
// localised mode — every country's wording with its approval.
//
// Where each piece lives, because the screen has to send people there:
//   * the letter is a published template version (PUT /letter);
//   * subject and preheader are the version's, per language — edited in the
//     template editor, shown here;
//   * the A/B arms override the subject (PUT/DELETE /ab);
//   * localised mode: each country's text is a localisation row that has to be
//     approved before the campaign can be scheduled — the gate is the API's
//     (4.3: a condition inside the `review → scheduled` transition), and this
//     step shows it as a gate;
//   * localised mode with an A/B test: every arm's subject line is worded per
//     country too — a matrix, countries by arms, each cell approved on its own
//     (the owner's decision of 2026-09-29), and the same gate waits for all.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLinkIcon, PlusIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  approveArmWording,
  approveCountryWording,
  approveLocalization,
  clearGroupAb,
  editArmWording,
  editLocalization,
  fetchTemplate,
  fetchTemplates,
  fetchVersion,
  requestTranslation,
  setGroupAb,
  setGroupLetter,
  withdrawArmWording,
  withdrawLocalization,
} from "@/lib/api";
import type {
  CountryWording,
  Localization,
  Template,
  TemplateVariant,
  TemplateVersion,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { labelOf } from "@/components/emc/status";
import type { Editor } from "@/pages/campaigns/editor/context";
import { COMPOSING, messageOf, wordingStatusOf } from "@/pages/campaigns/model";
import { StepActions } from "@/pages/campaigns/parts";
import { Country } from "@/components/emc/Flag";

export function ContentStep({ editor }: { editor: Editor }) {
  const { summary } = editor;
  const [letterChoice, setLetterChoice] = useState<number | null>(
    summary.letter?.template_version_id ?? null,
  );
  const [ab, setAb] = useState<AbForm>(() => abFormOf(editor));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A reload elsewhere (another tab, a restart) moves the saved state; the
  // form follows it unless the person has started changing it.
  const savedLetter = summary.letter?.template_version_id ?? null;
  useEffect(() => setLetterChoice(savedLetter), [savedLetter]);

  const letterDirty = letterChoice !== null && letterChoice !== savedLetter;
  const abDirty = abChanged(ab, editor);
  const abProblem = ab.on ? abFormProblem(ab) : null;

  async function saveLetter(): Promise<boolean> {
    if (!letterDirty || letterChoice === null) return true;
    try {
      await setGroupLetter(editor.groupId, letterChoice);
      await editor.reload();
      toast.success("A levél beállítva");
      return true;
    } catch (raised: unknown) {
      setError(messageOf(raised));
      return false;
    }
  }

  async function saveAb(): Promise<boolean> {
    if (!abDirty) return true;
    try {
      if (ab.on) {
        if (abProblem !== null) {
          setError(abProblem);
          return false;
        }
        await setGroupAb(
          editor.groupId,
          ab.arms.map((arm) => ({
            key: arm.key,
            subject: arm.subject.trim(),
            preheader: arm.preheader.trim(),
            share_permille: Math.round(Number(arm.percent) * 10),
          })),
          Number(ab.hours),
          ab.metric,
        );
        toast.success("Az A/B teszt beállítva");
      } else {
        await clearGroupAb(editor.groupId);
        toast.success("Az A/B teszt kikapcsolva");
      }
      await editor.reload();
      return true;
    } catch (raised: unknown) {
      setError(messageOf(raised));
      return false;
    }
  }

  async function saveAll(): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      return (await saveLetter()) && (await saveAb());
    } finally {
      setBusy(false);
    }
  }

  // A template picked without a version is a letter half-chosen: going on
  // would quietly keep the old one.
  const nextReason =
    letterChoice === null
      ? savedLetter === null
        ? "Válassz levelet: egy sablon publikált verzióját."
        : "Válassz verziót a kiválasztott sablonból, vagy válaszd vissza az eredeti sablont."
      : abDirty && abProblem !== null
        ? abProblem
        : null;

  return (
    <div className="flex flex-col gap-6">
      {error !== null && <ErrorState message={error} />}
      <LetterCard editor={editor} choice={letterChoice} onChoose={setLetterChoice} />
      {summary.mode === "localized" ? (
        <LocalizationsCard editor={editor} />
      ) : (
        <SubjectCard editor={editor} />
      )}
      <AbCard editor={editor} form={ab} onChange={setAb} problem={abProblem} dirty={abDirty} />
      {summary.mode === "localized" && summary.ab !== null && summary.ab_arms.length > 0 && (
        <ArmMatrixCard editor={editor} />
      )}
      <StepActions
        onBack={() => editor.go("cimzettek")}
        next={{
          label: letterDirty || abDirty ? "Mentés és tovább" : "Tovább az időzítéshez",
          onClick: () =>
            void saveAll().then((saved) => {
              if (saved) editor.go("idozites");
            }),
          disabled: nextReason !== null,
          reason: nextReason,
          busy,
        }}
      />
    </div>
  );
}

// --- the letter -------------------------------------------------------------------

function LetterCard({
  editor,
  choice,
  onChoose,
}: {
  editor: Editor;
  choice: number | null;
  onChoose: (versionId: number | null) => void;
}) {
  const { summary, editable } = editor;
  const letter = summary.letter;
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(letter?.template_id ?? null);
  const [versions, setVersions] = useState<TemplateVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates()
      .then(setTemplates)
      .catch((raised: unknown) => setError(messageOf(raised)));
  }, []);

  useEffect(() => {
    if (templateId === null) {
      setVersions(null);
      return;
    }
    setVersions(null);
    fetchTemplate(templateId)
      .then((found) => setVersions(found.versions))
      .catch((raised: unknown) => setError(messageOf(raised)));
  }, [templateId]);

  const published = (versions ?? []).filter((version) => version.status === "published");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Levél</CardTitle>
        <CardDescription>
          Egy sablon egy publikált verziója. A tartalmat a sablon-szerkesztőben lehet módosítani;
          ott új verzió születik, amit itt kell kiválasztani.
        </CardDescription>
        {letter !== null && (
          <CardAction>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/sablonok/${letter.template_id}`}>
                <ExternalLinkIcon />
                Megnyitás a szerkesztőben
              </Link>
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} />}
        {letter !== null ? (
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">Most:</span>
            <span className="font-medium">{letter.template_name}</span>
            <span className="data-mono text-muted-foreground">v{letter.version}</span>
            <StatusPill domain="template" state={letter.status} />
          </p>
        ) : (
          <p className="text-muted-foreground">Még nincs levél kiválasztva.</p>
        )}
        {editable && (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Sablon" htmlFor="letter-template">
              <NativeSelect
                id="letter-template"
                className="w-full"
                value={templateId === null ? "" : String(templateId)}
                onChange={(event) => {
                  const next = event.target.value === "" ? null : Number(event.target.value);
                  setTemplateId(next);
                  onChoose(next === letter?.template_id ? letter.template_version_id : null);
                }}
              >
                <option value="">{templates === null ? "Betöltés…" : "— válassz sablont —"}</option>
                {(templates ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Verzió"
              htmlFor="letter-version"
              hint={
                versions !== null && published.length === 0
                  ? "Ennek a sablonnak nincs publikált verziója. Publikáld a szerkesztőben."
                  : "Csak publikált verzió küldhető ki."
              }
            >
              <NativeSelect
                id="letter-version"
                className="w-full"
                value={choice === null ? "" : String(choice)}
                disabled={templateId === null}
                onChange={(event) =>
                  onChoose(event.target.value === "" ? null : Number(event.target.value))
                }
              >
                <option value="">
                  {templateId !== null && versions === null ? "Betöltés…" : "— válassz verziót —"}
                </option>
                {published
                  .slice()
                  .sort((a, b) => b.version - a.version)
                  .map((version) => (
                    <option key={version.id} value={version.id}>
                      v{version.version}
                      {version.published_at
                        ? ` — publikálva ${new Date(version.published_at).toLocaleDateString("hu-HU")}`
                        : ""}
                    </option>
                  ))}
              </NativeSelect>
            </Field>
          </div>
        )}
        {editable && summary.mode === "localized" && letter !== null && (
          <p className="text-label text-muted-foreground">
            A levél cseréje után a már jóváhagyott fordítások elavulnak: a fordítást újra kell
            kérni és jóváhagyni.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// --- subject and preheader, single-language mode ------------------------------------

function SubjectCard({ editor }: { editor: Editor }) {
  const letter = editor.summary.letter;
  const [variants, setVariants] = useState<TemplateVariant[] | null>(null);
  const [language, setLanguage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (letter === null) return;
    setVariants(null);
    fetchVersion(letter.template_id, letter.version)
      .then((found) => {
        setVariants(found.variants);
        setLanguage(found.default_language);
      })
      .catch((raised: unknown) => setError(messageOf(raised)));
  }, [letter]);

  const languages = useMemo(
    () =>
      editor.summary.countries.map((row) => ({
        country: row.country,
        language: editor.identityOf(row.country)?.language ?? "—",
      })),
    [editor],
  );

  const columns: ColumnDef<TemplateVariant>[] = [
    {
      id: "language",
      header: "Nyelv",
      cell: ({ row }) => (
        <span className="data-mono">
          {row.original.language}
          {row.original.country ? ` · ${row.original.country}` : ""}
          {row.original.language === language ? " (alap)" : ""}
        </span>
      ),
    },
    { id: "subject", header: "Tárgy", cell: ({ row }) => row.original.subject || "—", meta: { wrap: true } },
    {
      id: "preheader",
      header: "Előnézeti szöveg",
      cell: ({ row }) => row.original.preheader || "—",
      meta: { wrap: true, muted: true },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tárgy és előnézeti szöveg</CardTitle>
        <CardDescription>
          A sablon része, nyelvenként. Minden ország a saját nyelvű változatot kapja; ahol nincs
          ilyen, az alapnyelvűt. Az Áttekintés lépés országonként megmutatja, pontosan mi megy ki.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} />}
        {letter === null ? (
          <p className="text-muted-foreground">Előbb válassz levelet.</p>
        ) : variants === null ? (
          <LoadingState lines={2} />
        ) : (
          <DataTable
            label="A levél nyelvi változatai"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={columns}
            data={variants}
            getRowId={(row) => String(row.id)}
          />
        )}
        {languages.length > 0 && (
          <p className="text-muted-foreground">
            Az országok nyelve:{" "}
            {languages.map((entry, index) => (
              <span key={entry.country}>
                {index > 0 && ", "}
                <span className="data-mono text-ink">{entry.country}</span> → {entry.language}
              </span>
            ))}
            .
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// --- the A/B arms -------------------------------------------------------------------

interface ArmForm {
  key: string;
  subject: string;
  preheader: string;
  /** Share of the whole audience, in percent, as typed: "10". */
  percent: string;
}

interface AbForm {
  on: boolean;
  arms: ArmForm[];
  hours: string;
  metric: "open" | "click";
}

const ARM_KEYS = ["A", "B", "C"] as const;

function abFormOf(editor: Editor): AbForm {
  const { summary } = editor;
  const plan = summary.ab;
  if (plan === null || summary.ab_arms.length === 0) {
    return {
      on: plan !== null,
      arms: [
        { key: "A", subject: "", preheader: "", percent: "10" },
        { key: "B", subject: "", preheader: "", percent: "10" },
      ],
      hours: "4",
      metric: "open",
    };
  }
  return {
    on: true,
    arms: summary.ab_arms.map((arm) => ({
      key: arm.key,
      subject: arm.subject,
      preheader: arm.preheader,
      percent: String(arm.share_permille / 10),
    })),
    hours: typeof plan.decide_after_hours === "number" ? String(plan.decide_after_hours) : "4",
    metric: plan.metric === "click" ? "click" : "open",
  };
}

function abChanged(form: AbForm, editor: Editor): boolean {
  const saved = abFormOf(editor);
  if (form.on !== (editor.summary.ab !== null)) return true;
  if (!form.on) return false;
  return JSON.stringify(form) !== JSON.stringify(saved);
}

function abFormProblem(form: AbForm): string | null {
  if (form.arms.length < 2) return "Az A/B teszthez legalább két variáns kell.";
  const blank = form.arms.find((arm) => arm.subject.trim() === "");
  if (blank) return `A(z) ${blank.key} variáns tárgya üres. Adj meg tárgyat minden variánsnak.`;
  let total = 0;
  for (const arm of form.arms) {
    const value = Number(arm.percent);
    if (!Number.isFinite(value) || value < 0.1 || value > 99.9) {
      return `A(z) ${arm.key} variáns hányada 0,1 és 99,9 % közötti szám legyen.`;
    }
    total += Math.round(value * 10);
  }
  if (total >= 1000) {
    return "A variánsok együtt kevesebb mint 100%-ot kaphatnak: a maradék kapja a győztest.";
  }
  const hours = Number(form.hours);
  if (!Number.isFinite(hours) || hours <= 0 || hours > 168) {
    return "A döntésig eltelő idő 0 és 168 óra közötti szám legyen.";
  }
  return null;
}

function AbCard({
  editor,
  form,
  onChange,
  problem,
  dirty,
}: {
  editor: Editor;
  form: AbForm;
  onChange: (next: AbForm) => void;
  problem: string | null;
  dirty: boolean;
}) {
  const { editable } = editor;
  const testShare = form.arms.reduce((sum, arm) => sum + (Number(arm.percent) || 0), 0);
  const setArm = (index: number, patch: Partial<ArmForm>) =>
    onChange({
      ...form,
      arms: form.arms.map((arm, position) => (position === index ? { ...arm, ...patch } : arm)),
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>A/B teszt a tárgysorra</CardTitle>
        <CardDescription>
          A közönség egy része két vagy három tárggyal kap levelet. A maradék megvárja, melyik
          tárgy nyer, és a győztessel megy ki.
        </CardDescription>
        <CardAction>
          <label className="flex cursor-pointer items-center gap-2">
            <Switch
              checked={form.on}
              disabled={!editable}
              onCheckedChange={(on) => onChange({ ...form, on })}
              aria-label="A/B teszt"
            />
            {form.on ? "Be" : "Ki"}
          </label>
        </CardAction>
      </CardHeader>
      {form.on && (
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            {form.arms.map((arm, index) => (
              <div
                key={arm.key}
                className="grid gap-3 rounded-card border border-line p-3 md:grid-cols-[auto_minmax(0,3fr)_minmax(0,3fr)_minmax(0,1fr)_auto] md:items-end"
              >
                <span className="data-mono grid size-control place-items-center rounded-control bg-surface-2 font-semibold">
                  {arm.key}
                </span>
                <Field label="Tárgy" htmlFor={`arm-${arm.key}-subject`}>
                  <Input
                    id={`arm-${arm.key}-subject`}
                    value={arm.subject}
                    maxLength={500}
                    disabled={!editable}
                    onChange={(event) => setArm(index, { subject: event.target.value })}
                  />
                </Field>
                <Field label="Előnézeti szöveg (nem kötelező)" htmlFor={`arm-${arm.key}-preheader`}>
                  <Input
                    id={`arm-${arm.key}-preheader`}
                    value={arm.preheader}
                    maxLength={500}
                    disabled={!editable}
                    onChange={(event) => setArm(index, { preheader: event.target.value })}
                  />
                </Field>
                <Field label="Hányad (%)" htmlFor={`arm-${arm.key}-share`}>
                  <Input
                    id={`arm-${arm.key}-share`}
                    type="number"
                    min={0.1}
                    max={99.9}
                    step={0.1}
                    className="text-right"
                    value={arm.percent}
                    disabled={!editable}
                    onChange={(event) => setArm(index, { percent: event.target.value })}
                  />
                </Field>
                {form.arms.length > 2 && editable ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    aria-label={`A(z) ${arm.key} variáns törlése`}
                    onClick={() => onChange({ ...form, arms: form.arms.filter((_, position) => position !== index) })}
                  >
                    <Trash2Icon />
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
          {form.arms.length < 3 && editable && (
            <div>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() =>
                  onChange({
                    ...form,
                    arms: [
                      ...form.arms,
                      {
                        key: ARM_KEYS.find((key) => !form.arms.some((arm) => arm.key === key)) ?? "C",
                        subject: "",
                        preheader: "",
                        percent: "10",
                      },
                    ],
                  })
                }
              >
                <PlusIcon />
                Harmadik variáns
              </Button>
            </div>
          )}
          <p className="text-muted-foreground">
            A teszt a közönség{" "}
            <span className="font-medium text-ink">{testShare.toLocaleString("hu-HU")}%</span>-án fut
            (véletlen minta országonként); a maradék{" "}
            <span className="font-medium text-ink">
              {Math.max(0, 100 - testShare).toLocaleString("hu-HU")}%
            </span>{" "}
            a győztes tárggyal megy ki.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Döntés ennyi óra után"
              htmlFor="ab-hours"
              hint="Az első ország indulásától számítva; a kampány oldalán korábban is kihirdethető győztes."
            >
              <Input
                id="ab-hours"
                type="number"
                min={1}
                max={168}
                step={1}
                value={form.hours}
                disabled={!editable}
                onChange={(event) => onChange({ ...form, hours: event.target.value })}
              />
            </Field>
            <Field
              label="A döntés alapja"
              htmlFor="ab-metric"
              hint="Variánsonként az egyedi események száma a kiküldöttekhez képest."
            >
              <NativeSelect
                id="ab-metric"
                className="w-full"
                value={form.metric}
                disabled={!editable}
                onChange={(event) =>
                  onChange({ ...form, metric: event.target.value === "click" ? "click" : "open" })
                }
              >
                <option value="open">{labelOf("abMetric", "open")}i arány</option>
                <option value="click">{labelOf("abMetric", "click")}i arány</option>
              </NativeSelect>
            </Field>
          </div>
          {dirty && problem !== null && <Callout tone="warn">{problem}</Callout>}
        </CardContent>
      )}
      {!form.on && editor.summary.ab !== null && editable && (
        <CardContent>
          <Callout tone="warn">
            A tovább gombbal az A/B teszt kikapcsol, és a variánsok törlődnek.
          </Callout>
        </CardContent>
      )}
    </Card>
  );
}

// --- localised mode: every country's text, and the gate --------------------------------

function LocalizationsCard({ editor }: { editor: Editor }) {
  const { summary, detail, localizations } = editor;
  const approvable = COMPOSING.has(summary.state);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, { subject: string; preheader: string }>>({});

  useEffect(() => {
    void editor.reloadLocalizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const known = new Map((localizations ?? []).map((row) => [row.country, row] as const));
    return summary.countries.map((row) => ({ country: row.country, localization: known.get(row.country) ?? null }));
  }, [localizations, summary.countries]);

  async function act(work: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await Promise.all([editor.reloadLocalizations(), editor.reload()]);
      toast.success(done);
    } catch (raised: unknown) {
      setError(messageOf(raised));
      // A refused approval may be one whose source moved while it was read
      // (409): what is on screen is reloaded, so it is read again, not re-sent.
      await Promise.all([editor.reloadLocalizations(), editor.reload()]).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  const columns: ColumnDef<{ country: string; localization: Localization | null }>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => (
        <span className="data-mono font-medium">
          {row.original.country}
          {row.original.country === summary.source_country && (
            <span className="block text-label font-normal text-muted-foreground">forrás</span>
          )}
        </span>
      ),
    },
    {
      id: "text",
      header: "Tárgy · előnézeti szöveg",
      cell: ({ row }) => {
        const found = row.original.localization;
        if (found === null) return <span className="text-muted-foreground">Még nincs fordítás.</span>;
        const edit = edits[row.original.country];
        if (edit !== undefined) {
          return (
            <span className="flex flex-col gap-2">
              <Input
                aria-label={`${row.original.country} tárgya`}
                value={edit.subject}
                maxLength={500}
                onChange={(event) =>
                  setEdits((current) => ({ ...current, [row.original.country]: { ...edit, subject: event.target.value } }))
                }
              />
              <Input
                aria-label={`${row.original.country} előnézeti szövege`}
                value={edit.preheader}
                maxLength={500}
                onChange={(event) =>
                  setEdits((current) => ({ ...current, [row.original.country]: { ...edit, preheader: event.target.value } }))
                }
              />
              <span className="text-label text-muted-foreground">A mentés visszavonja a jóváhagyást.</span>
            </span>
          );
        }
        return (
          <span className="flex flex-col">
            <span>{found.subject || <em className="text-muted-foreground">üres tárgy</em>}</span>
            <span className="text-label text-muted-foreground">{found.preheader || "—"}</span>
          </span>
        );
      },
      meta: { wrap: true, className: "w-full" },
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => {
        const found = row.original.localization;
        const state = wordingStatusOf(found, summary.stale.includes(row.original.country));
        return (
          <span className="flex flex-col items-start gap-1">
            <StatusPill domain="wording" state={state} title={found?.approved_by ?? undefined} />
            {found?.approved_by && <span className="text-label text-muted-foreground">{found.approved_by}</span>}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) => {
        const found = row.original.localization;
        const country = row.original.country;
        if (found === null || !approvable) return null;
        const edit = edits[country];
        if (edit !== undefined) {
          return (
            <span className="flex justify-end gap-2">
              <Button variant="outline" size="sm" type="button" disabled={busy}
                onClick={() => setEdits((current) => { const next = { ...current }; delete next[country]; return next; })}>
                Mégse
              </Button>
              <Button size="sm" type="button" disabled={busy || edit.subject.trim() === ""}
                onClick={() =>
                  void act(async () => {
                    await editLocalization(editor.groupId, country, { subject: edit.subject, preheader: edit.preheader });
                    setEdits((current) => { const next = { ...current }; delete next[country]; return next; });
                  }, `A(z) ${country} szövege mentve — a jóváhagyás visszavonva`)
                }>
                Mentés
              </Button>
            </span>
          );
        }
        return (
          <span className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" type="button" disabled={busy}
              onClick={() => setEdits((current) => ({ ...current, [country]: { subject: found.subject, preheader: found.preheader } }))}>
              Javítás
            </Button>
            {found.approved_at === null || summary.stale.includes(country) ? (
              // A stale approval is of an older source and holds the schedule
              // like none: it is given again, not withdrawn first.
              <Button size="sm" type="button" disabled={busy || found.subject.trim() === ""}
                onClick={() => void act(() => approveLocalization(editor.groupId, country, found.source_digest), `A(z) ${country} szövege jóváhagyva`)}>
                {found.approved_at === null ? "Jóváhagyom" : "Újra jóváhagyom"}
              </Button>
            ) : (
              <Button variant="outline" size="sm" type="button" disabled={busy}
                onClick={() => void act(() => withdrawLocalization(editor.groupId, country), `A(z) ${country} jóváhagyása visszavonva`)}>
                Visszavonom
              </Button>
            )}
          </span>
        );
      },
      meta: { align: "right" },
    },
  ];

  const missing = detail.unapproved;
  const stale = summary.stale.filter((country) => summary.countries.some((row) => row.country === country));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Szöveg országonként</CardTitle>
        <CardDescription>
          A forrásország ({summary.source_country ?? "—"}) szövegéből gépi fordítás készül a
          többi országnak. Minden ország szövegét egy embernek el kell olvasnia és jóvá kell hagynia.
        </CardDescription>
        <CardAction>
          <Button variant="outline" size="sm" type="button" disabled={busy}
            onClick={() => void act(() => editor.reloadLocalizations(), "A szövegek frissítve")}>
            <RefreshCwIcon />
            Frissítés
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {missing.length > 0 ? (
          <Callout tone="warn">
            <strong className="font-medium">Jóváhagyás nélkül nem küldhető ki.</strong> Hiányzik:{" "}
            <span className="data-mono">{missing.join(", ")}</span>. Ez a feltétel a rendszerben van, nem
            csak ezen a képernyőn: amíg bármelyik ország hiányzik, a kampány nem ütemezhető.
          </Callout>
        ) : stale.length > 0 ? (
          <Callout tone="warn">
            <strong className="font-medium">Elavult jóváhagyás:</strong>{" "}
            <span className="data-mono">{stale.join(", ")}</span>. A forrás (a levél vagy a
            forrásszöveg) a jóváhagyás óta változott; amíg újra jóvá nem hagyod, a kampány nem
            ütemezhető.
          </Callout>
        ) : (
          summary.countries.length > 0 && <Callout tone="success">Minden ország szövege jóváhagyva.</Callout>
        )}
        {error !== null && <ErrorState message={error} />}
        {localizations === null ? (
          <LoadingState lines={3} />
        ) : (
          <DataTable
            label="Szöveg országonként"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={columns}
            data={rows}
            getRowId={(row) => row.country}
            empty={<p className="px-4 py-6 text-center text-muted-foreground">Előbb válassz országokat.</p>}
          />
        )}
        {approvable && summary.letter !== null && (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" disabled={busy}
              onClick={() =>
                void act(async () => {
                  const answer = await requestTranslation(editor.groupId);
                  toast.info(
                    answer.countries.length === 0
                      ? "Nincs mit fordítani: minden ország jóváhagyva."
                      : `Fordítás kérve: ${answer.countries.join(", ")}`,
                    { description: `A fordítást az üzemeltető indítja: ${answer.command}` },
                  );
                }, "A fordítás kérése elküldve")
              }>
              Fordítás kérése
            </Button>
            <span className="text-label text-muted-foreground">
              A fordítás a háttérben készül, országonként egy-két perc; utána a „Frissítés” hozza be.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- localised A/B: every arm's subject line, per country ------------------------------

type CellEdit = { subject: string; preheader: string };

function ArmMatrixCard({ editor }: { editor: Editor }) {
  const { summary, wording } = editor;
  const approvable = COMPOSING.has(summary.state);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, CellEdit>>({});
  const keys = summary.ab_arms.map((arm) => arm.key);
  const rows = wording ?? [];
  const total = rows.length * keys.length;
  const approved = rows.reduce(
    (sum, row) => sum + row.arms.filter((arm) => arm.status === "approved").length,
    0,
  );
  const missing = summary.unapproved_arms;

  const forget = (cell: string) =>
    setEdits((current) => {
      const next = { ...current };
      delete next[cell];
      return next;
    });

  async function act(work: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await Promise.all([editor.reloadLocalizations(), editor.reload()]);
      toast.success(done);
    } catch (raised: unknown) {
      setError(messageOf(raised));
      // A refused approval may be one whose source moved while it was read
      // (409): what is on screen is reloaded, so it is read again, not re-sent.
      await Promise.all([editor.reloadLocalizations(), editor.reload()]).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  function armCell(row: CountryWording, key: string) {
    const country = row.country;
    const cell = `${country}/${key}`;
    const arm = row.arms.find((entry) => entry.key === key);
    if (arm === undefined) return <span className="text-muted-foreground">—</span>;
    const edit = edits[cell];
    if (edit !== undefined) {
      return (
        <span className="flex min-w-48 flex-col gap-2">
          <Input
            aria-label={`${cell} tárgya`}
            value={edit.subject}
            maxLength={500}
            onChange={(event) =>
              setEdits((current) => ({ ...current, [cell]: { ...edit, subject: event.target.value } }))
            }
          />
          <Input
            aria-label={`${cell} előnézeti szövege`}
            placeholder="az ország saját előnézeti szövege"
            value={edit.preheader}
            maxLength={500}
            onChange={(event) =>
              setEdits((current) => ({ ...current, [cell]: { ...edit, preheader: event.target.value } }))
            }
          />
          <span className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" type="button" disabled={busy} onClick={() => forget(cell)}>
              Mégse
            </Button>
            <Button
              size="sm"
              type="button"
              disabled={busy || edit.subject.trim() === ""}
              onClick={() =>
                void act(async () => {
                  await editArmWording(editor.groupId, country, key, {
                    subject: edit.subject,
                    preheader: edit.preheader,
                  });
                  forget(cell);
                }, `${cell} mentve — a jóváhagyás visszavonva`)
              }
            >
              Mentés
            </Button>
          </span>
          <span className="text-label text-muted-foreground">A mentés visszavonja a jóváhagyást.</span>
        </span>
      );
    }
    return (
      <span className="flex min-w-48 flex-col items-start gap-1">
        {arm.status === "missing" ? (
          <span className="text-muted-foreground">Még nincs szöveg.</span>
        ) : (
          <span className="flex flex-col">
            <span>{arm.subject || <em className="text-muted-foreground">üres tárgy</em>}</span>
            {arm.preheader && <span className="text-label text-muted-foreground">{arm.preheader}</span>}
          </span>
        )}
        <StatusPill
          domain="wording"
          state={arm.status}
          title={arm.approved_by ? `${arm.approved_by}, ${arm.approved_at ?? ""}` : undefined}
        />
        {approvable && (
          <span className="flex flex-wrap gap-1">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              disabled={busy}
              onClick={() =>
                setEdits((current) => ({
                  ...current,
                  [cell]: { subject: arm.subject, preheader: arm.preheader },
                }))
              }
            >
              {arm.status === "missing" ? "Megírom" : "Javítás"}
            </Button>
            {arm.status === "approved" ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => withdrawArmWording(editor.groupId, country, key),
                    `${cell} jóváhagyása visszavonva`,
                  )
                }
              >
                Visszavonom
              </Button>
            ) : (
              arm.status !== "missing" && (
                <Button
                  size="sm"
                  type="button"
                  disabled={busy || arm.subject.trim() === ""}
                  onClick={() =>
                    void act(
                      () => approveArmWording(editor.groupId, country, key, arm.source_digest),
                      `${cell} jóváhagyva`,
                    )
                  }
                >
                  Jóváhagyás
                </Button>
              )
            )}
          </span>
        )}
      </span>
    );
  }

  const columns: ColumnDef<CountryWording>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <Country code={row.original.country} />
          <span className="data-mono text-label text-muted-foreground">
            {row.original.language}
            {row.original.country === summary.source_country && " · forrás"}
          </span>
        </span>
      ),
    },
    ...keys.map(
      (key): ColumnDef<CountryWording> => ({
        id: `arm-${key}`,
        header: () => <span className="data-mono">{key}</span>,
        cell: ({ row }) => armCell(row.original, key),
        meta: { wrap: true },
      }),
    ),
    {
      id: "all",
      header: () => <span className="sr-only">Mind jóváhagyása</span>,
      cell: ({ row }) => {
        const country = row.original.country;
        const open =
          row.original.status !== "approved" ||
          row.original.arms.some((arm) => arm.status !== "approved");
        if (!approvable || !open) return null;
        return (
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={busy}
            onClick={() =>
              void act(
                () => approveCountryWording(editor.groupId, row.original),
                `A(z) ${country} szövege és minden variánsa jóváhagyva`,
              )
            }
          >
            Mind jóváhagyása
          </Button>
        );
      },
      meta: { align: "right" },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>A/B tárgysorok országonként</CardTitle>
        <CardDescription>
          Minden ország minden variánsát külön kell jóváhagyni. A forrásország (
          {summary.source_country ?? "—"}) variánsai a fenti A/B-kártya szövegei; a többi országé
          gépi fordítás, amit itt javíthatsz. Ha egy forrásvariáns változik, a rá adott
          jóváhagyások elavulnak.
        </CardDescription>
        <CardAction>
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={busy}
            onClick={() => void act(() => editor.reloadLocalizations(), "A variánsok frissítve")}
          >
            <RefreshCwIcon />
            Frissítés
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {missing.length > 0 ? (
          <Callout tone="warn">
            <strong className="font-medium">
              {approved} / {total} variáns jóváhagyva.
            </strong>{" "}
            Hiányzik vagy elavult: <span className="data-mono">{missing.join(", ")}</span>. Amíg
            bármelyik hiányzik, a kampány nem ütemezhető — a feltétel a rendszerben van, nem csak
            ezen a képernyőn.
          </Callout>
        ) : (
          total > 0 && <Callout tone="success">Minden ország minden variánsa jóváhagyva.</Callout>
        )}
        {error !== null && <ErrorState message={error} />}
        {wording === null ? (
          <LoadingState lines={3} />
        ) : (
          <DataTable
            label="A/B tárgysorok országonként"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={columns}
            data={rows}
            getRowId={(row) => row.country}
            empty={<p className="px-4 py-6 text-center text-muted-foreground">Előbb válassz országokat.</p>}
          />
        )}
      </CardContent>
    </Card>
  );
}
